import {signal, Signal} from '@angular/core';
import {EMPTY, Observable, Subject, forkJoin, of} from 'rxjs';
import {catchError, map, switchMap, takeUntil} from 'rxjs/operators';
import {AnomalyPolygonMapDetailDto} from '@6grain/masofa-monolith-api-client/satellite-indices';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {SatelliteProductsService} from '@services/satellite-products.service';

export type AnomalyImagerySourceType = 'Sentinel2' | 'Landsat';

export interface SyncedAnomalyDate {
  anomalyId: string;
  /** YYYY-MM-DD actually applied to the timeline + map layer; null when nothing was applied. */
  date: string | null;
  /** YYYY-MM-DD derived from the anomaly's own originalDate, before any fallback; null when unparseable. */
  requestedDate: string | null;
  isFallback: boolean;
  noImageryAvailable: boolean;
  /** True when the clicked anomaly's own `originalDate` was missing or unparseable. */
  invalidDate: boolean;
  /** True when the `getAvailableDates` lookup itself failed (network/server error). */
  loadFailed: boolean;
}

export interface AnomalyDateSyncHost {
  openstreetmapComponent: OpenStreetMapComponent | undefined;
  satelliteProducts: SatelliteProductsService;
}

interface PendingClick {
  anomalyId: string;
  /** null when the anomaly's own originalDate was missing or unparseable. */
  requestedDate: string | null;
  sourceType: AnomalyImagerySourceType;
}

type PartialSync = Pick<
  SyncedAnomalyDate,
  'anomalyId' | 'date' | 'requestedDate' | 'isFallback' | 'noImageryAvailable'
>;

/** Custom/GroundSurvey have no imagery layer of their own; Sentinel2 is the platform default. */
function resolveEffectiveSourceType(
  productSourceType: string | null | undefined,
): AnomalyImagerySourceType {
  return productSourceType === 'Landsat' ? 'Landsat' : 'Sentinel2';
}

/** Calendar day (YYYY-MM-DD) of an ISO date/datetime string, or null when unparseable. */
function toCalendarDate(originalDate: string | null | undefined): string | null {
  if (!originalDate) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(originalDate);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const parsed = new Date(originalDate);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  // UTC getters, not local: the ISO-prefix branch above never applies a timezone, and this
  // fallback must not silently drift a day depending on the viewer's own timezone offset.
  const y = parsed.getUTCFullYear();
  const m = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const d = String(parsed.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function daysBetween(a: string, b: string): number {
  const msPerDay = 86_400_000;
  return Math.round((Date.parse(a) - Date.parse(b)) / msPerDay);
}

/** Nearest date by absolute day difference; ties break toward the earlier date, every time. */
export function findNearestAvailableDate(dates: readonly string[], target: string): string | null {
  let best: string | null = null;
  let bestDiff = Infinity;
  for (const date of dates) {
    const diff = Math.abs(daysBetween(date, target));
    if (diff < bestDiff || (diff === bestDiff && best !== null && date < best)) {
      best = date;
      bestDiff = diff;
    }
  }
  return best;
}

/**
 * Calendar years to search so a target near Jan 1 / Dec 31 can still find the nearest available
 * date across the year boundary — a plain single-year query would otherwise silently miss a date
 * just one day into the adjacent year (docs/ANOMALY-MAP-TIMELINE-SYNC.md).
 */
export function yearsToQuery(requestedDate: string): number[] {
  const year = Number(requestedDate.slice(0, 4));
  const monthDay = requestedDate.slice(5);
  const years = [year];
  if (monthDay <= '01-15') {
    years.unshift(year - 1);
  }
  if (monthDay >= '12-17') {
    years.push(year + 1);
  }
  return years;
}

/**
 * Orchestrates clicking an anomaly on the GIS map into synchronizing the satellite timeline and
 * map imagery to that anomaly's detection date (docs/ANOMALY-MAP-TIMELINE-SYNC.md). Not
 * `@Injectable` — instantiated once per `GisAnomaliesComponent`, following the same
 * `getHost`-closure pattern as `SatelliteTimelineManager`.
 */
export class AnomalyDateSyncManager {
  private readonly clicks$ = new Subject<PendingClick>();
  private readonly destroy$ = new Subject<void>();

  private readonly _syncedAnomalyDate = signal<SyncedAnomalyDate | null>(null);
  private readonly _syncing = signal(false);
  readonly syncedAnomalyDate: Signal<SyncedAnomalyDate | null> = this._syncedAnomalyDate.asReadonly();
  readonly syncing: Signal<boolean> = this._syncing.asReadonly();

  constructor(private readonly getHost: () => AnomalyDateSyncHost) {
    // Every click — including one with a missing/invalid date — flows through this single
    // switchMap, so a later click always supersedes an earlier one's full resolution (HTTP lookup
    // AND the wait for the target year to load AND applying it), not just its HTTP request.
    this.clicks$
      .pipe(
        switchMap((click) => this.resolveClick(click)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  /** Called from the anomaly marker/polygon onClick handler in AnomalyMapManagerService. */
  onAnomalyClicked(row: AnomalyPolygonMapDetailDto): void {
    const requestedDate = toCalendarDate(row.originalDate);
    const sourceType = resolveEffectiveSourceType(row.productSourceType);
    this._syncing.set(true);
    this.clicks$.next({anomalyId: row.id ?? '', requestedDate, sourceType});
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveClick({anomalyId, requestedDate, sourceType}: PendingClick): Observable<void> {
    if (!requestedDate) {
      return of(undefined).pipe(
        map(() => {
          this.report({
            anomalyId,
            date: null,
            requestedDate: null,
            isFallback: false,
            noImageryAvailable: false,
            invalidDate: true,
            loadFailed: false,
          });
        }),
      );
    }

    const host = this.getHost();
    const lookups = yearsToQuery(requestedDate).map((year) =>
      host.satelliteProducts.getAvailableDates({
        satelliteType: sourceType,
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`,
      }),
    );

    return forkJoin(lookups).pipe(
      switchMap((responses) => {
        const dates = responses
          .flatMap((response) => response.dates ?? [])
          .map((entry) => entry.date)
          .filter((date): date is string => !!date);

        if (dates.includes(requestedDate)) {
          return this.applyAndReport(host, requestedDate, sourceType, {
            anomalyId,
            date: requestedDate,
            requestedDate,
            isFallback: false,
            noImageryAvailable: false,
          });
        }

        const nearest = findNearestAvailableDate(dates, requestedDate);
        if (nearest) {
          return this.applyAndReport(host, nearest, sourceType, {
            anomalyId,
            date: nearest,
            requestedDate,
            isFallback: true,
            noImageryAvailable: false,
          });
        }

        return of(undefined).pipe(
          map(() => {
            this.report({
              anomalyId,
              date: requestedDate,
              requestedDate,
              isFallback: false,
              noImageryAvailable: true,
              invalidDate: false,
              loadFailed: false,
            });
          }),
        );
      }),
      catchError(() => {
        // Load failure: map/timeline stay put, but this click still gets its own error state
        // rather than leaving a previous anomaly's indicator on screen (docs/ANOMALY-MAP-TIMELINE-SYNC.md).
        this.report({
          anomalyId,
          date: null,
          requestedDate,
          isFallback: false,
          noImageryAvailable: false,
          invalidDate: false,
          loadFailed: true,
        });
        return EMPTY;
      }),
    );
  }

  /**
   * Applies the resolved date to the map/timeline and only THEN reports success — `focusDate`
   * returns an Observable that doesn't complete until the target year has actually finished
   * loading and `setRange` has run, so `syncing` stays true for the map's entire transition, not
   * just the initial HTTP lookup, and so the outer `switchMap` can cancel a still-pending
   * activation (not just its HTTP request) when a later click supersedes it.
   */
  private applyAndReport(
    host: AnomalyDateSyncHost,
    date: string,
    sourceType: AnomalyImagerySourceType,
    partial: PartialSync,
  ): Observable<void> {
    const activation$ = host.openstreetmapComponent?.focusDate(date, sourceType) ?? of(undefined);
    return activation$.pipe(
      map(() => this.report({...partial, invalidDate: false, loadFailed: false})),
    );
  }

  private report(state: SyncedAnomalyDate): void {
    this._syncedAnomalyDate.set(state);
    this._syncing.set(false);
  }
}
